import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, PageLayout, Card, Input, Button, ErrorBanner, MutedText, Label } from "../components/ui";
import { Logo } from "../components/Logo";
import { requestMagicLink, devLoginUrl } from "../lib/dataSource";
import { isStandalonePwa } from "../lib/platform";
import { isNative } from "../native/platform";

type State = "idle" | "loading" | "sent" | "error";

function MagicLinkForm({ state, email, setEmail, submit }: {
  state: State;
  email: string;
  setEmail: (v: string) => void;
  submit: (e: FormEvent) => void;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <form onSubmit={submit} className="flex flex-col gap-3">
        {state !== "sent" && (
          <MutedText>{t("login.noAccountNeeded")}</MutedText>
        )}
        <div>
          <Label>{t("login.emailLabel")}</Label>
          <Input
            type="email"
            required
            autoComplete="email"
            placeholder={t("login.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {state === "error" && <ErrorBanner>{t("login.error")}</ErrorBanner>}
        {state === "sent" ? (
          <p className="text-mint text-sm font-heading font-semibold text-center py-2">
            {t("login.sent")}
          </p>
        ) : (
          <Button type="submit" disabled={state === "loading"} className="w-full">
            {state === "loading" ? t("login.sending") : t("login.submit")}
          </Button>
        )}
      </form>
    </Card>
  );
}

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Arrivée depuis Réglages après « Supprimer mon compte ».
  const accountDeleted = (useLocation().state as { accountDeleted?: boolean } | null)?.accountDeleted === true;
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>("idle");
  // An installed iOS PWA never receives the magic link (it opens in
  // Safari); a store app does not either until its universal links / App
  // Links are live (docs/stores.md). Both put the code first.
  const native = isNative();
  const standalone = isStandalonePwa() || native;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setState("loading");
    try {
      await requestMagicLink(email);
      setState("sent");
    } catch {
      setState("error");
    }
  }

  const codeButton = (
    <Button type="button" variant={standalone ? "primary" : "secondary"} className="w-full" onClick={() => navigate("/login/code")}>
      {t("login.haveCode")}
    </Button>
  );

  return (
    <PageLayout>
      <Container width="narrow" className="flex min-h-screen flex-col justify-center gap-6 py-10">
        <div className="text-center">
          <Logo size={56} className="mb-3" />
          <h1 className="headline text-3xl">{t("common.appName")}</h1>
          <MutedText className="mt-2">{t("login.subtitle")}</MutedText>
        </div>

        {accountDeleted && (
          <div role="status">
            <Card>
              <p className="text-sm text-paper">{t("login.accountDeleted")}</p>
            </Card>
          </div>
        )}

        {standalone ? (
          <>
            {codeButton}
            <MutedText className="text-center -mt-3">{t(native ? "login.nativeHint" : "login.standaloneHint")}</MutedText>
            <MagicLinkForm state={state} email={email} setEmail={setEmail} submit={submit} />
          </>
        ) : (
          <>
            <MagicLinkForm state={state} email={email} setEmail={setEmail} submit={submit} />
            {codeButton}
          </>
        )}

        {import.meta.env.DEV && (
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            onClick={() => {
              window.location.href = devLoginUrl;
            }}
          >
            {t("login.devLogin")}
          </Button>
        )}
        <p className="text-center text-violet-muted text-xs font-heading uppercase tracking-wide">
          {t("login.signature")}
        </p>
      </Container>
    </PageLayout>
  );
}
